import {
  Box,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  type PartProps,
} from '@frontend/legacy-components';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupCollectionContext } from 'utils/multisite/UserGroupCollectionProvider';

const ThumbnailWarning = ({ text, color }: { text: string; color: string }) => (
  <Box
    background={color}
    top="0"
    borderTopLeftRadius="md"
    height={3}
    position="absolute"
    textAlign="center"
    width="100%"
  >
    <Box
      as="span"
      color="white"
      fontSize="sm"
      fontWeight="heavy"
      letterSpacing="1px"
      textTransform="uppercase"
    >
      {text}
    </Box>
  </Box>
);

/**
 * Derives Part component highlight prop from search result highlight format.
 * Part expects { number?, filename?, rev? } with query strings; search has
 * partNumber/revision (and partName) as arrays of strings with <em> tags.
 */
/** Extract only the text inside <em> tags (the highlighted match) for use as Part highlight query. */
const getEmContent = (raw: string) => {
  if (typeof raw !== 'string') return undefined;
  const matches = raw.match(/<em>([^<]*)<\/em>/g);
  if (!matches?.length) return undefined;
  return matches.map((m) => m.replace(/<\/?em>/g, '')).filter(Boolean)[0];
};

const getPartHighlight = (partResult: {
  data: any;
  highlight: { partNumber: string[]; revision: string[]; partName: string[] };
}) => {
  if (!partResult?.highlight) return undefined;
  const number = getEmContent(partResult.highlight.partNumber?.[0]);
  const rev = getEmContent(partResult.highlight.revision?.[0]);
  const filename = getEmContent(partResult.highlight.partName?.[0]);
  if (!number && !rev && !filename) return undefined;
  return {
    number: number || undefined,
    rev: rev || undefined,
    filename: filename || undefined,
  };
};

const PartCell = ({
  primaryFileUuid,
  filename,
  containsVirus,
  partResult, // { data: {}, highlight: {} }
  href,
  customWarning: { text: customWarningText, color: customWarningColor } = {},
  resultOwnerId,
  ...restProps
}: {
  primaryFileUuid: string;
  filename: string;
  containsVirus: boolean;
  partResult: {
    data: any;
    highlight: { partNumber: string[]; revision: string[]; partName: string[] };
  };
  href: string;
  customWarning: { text?: string; color?: string };
  resultOwnerId: number;
} & PartProps) => {
  const {
    partNumber,
    revision,
    exportControlled: isExportControlled,
    isAssembly,
  } = partResult?.data ?? {};

  const linkStyleProps = {
    _hover: { bg: 'interaction.surfaceHover', color: 'feature.primary' },
    _focusVisible: {
      outline: 'none',
      boxShadow: 'focus',
      bg: 'interaction.surfaceHover',
    },
    _active: { bg: 'interaction.surfaceHover' },
  };

  const activeGroup = useAppSelector(getActiveGroup);
  const isFromOutsideGroup = resultOwnerId !== activeGroup.id;
  const { initChangeSite } = useUserGroupCollectionContext();

  const linkProps = href
    ? isFromOutsideGroup
      ? {
          as: 'a',
          onClick: () =>
            initChangeSite({
              nextUrl: href,
              userGroupId: resultOwnerId,
            }),
          ...linkStyleProps,
        }
      : {
          as: 'a',
          href: href,
          ...linkStyleProps,
        }
    : {};

  return (
    <Part
      number={partNumber ?? null}
      filename={filename ?? null}
      rev={revision ?? null}
      isItar={!!isExportControlled}
      highlight={getPartHighlight(partResult)}
      size="xl"
      variant="default"
      {...linkProps}
      {...restProps}
    >
      <PartThumbnailContainer position="relative">
        <PartThumbnail
          containsVirus={containsVirus}
          filename={filename}
          partFileUuid={primaryFileUuid}
          isAssembly={isAssembly}
        />
        {customWarningText && (
          <ThumbnailWarning
            color={customWarningColor ?? 'alert.error'}
            text={customWarningText}
          />
        )}
      </PartThumbnailContainer>
      <PartLabel />
    </Part>
  );
};

export default PartCell;
